import { Button, SearchInput } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { useFetchAudiences } from '@mm/state/hooks/use-fetch-audiences';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AudienceAccordionItem } from '../AudienceAccordionItem/AudienceAccordionItem';
import { ReactComponent as EmptyIcon } from 'assets/empty-icon.svg';
import styles from './AudienceListAccordions.module.css';
import classNames from 'classnames';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../common/components/PrimarySelect/PrimarySelect';
import { CheckOutlined } from '@ant-design/icons';
import emptyStateImg from './images/audiences-empty-image.png';
import { useLeadContactsFilter } from '../use-lead-contacts-filter';
import { StoreState } from '@mm/state/store-interfaces';
import { orderBy } from 'lodash';
import { Pagination } from 'antd';

export const AudienceListAccordions = ({
  processClick,
  fetchContacts,
  onCreateClick,
  activeAudienceId,
  busy,
}: {
  activeAudienceId?: number;
  busy?: boolean;
  onCreateClick: () => void;
  fetchContacts: (
    pageSize?: number,
    pageNumber?: number,
    query?: string,
  ) => void;
  processClick: (onSuccess: (args: any) => void) => void;
}) => {
  const audiencesEntities = useSelector(
    (state: StoreState) => state.audiences.entities,
  );
  const audienceDetails = useSelector(
    (state: StoreState) => state.audiences.audienceDetails,
  );
  const audiencesSorted = orderBy(
    audiencesEntities,
    aud => aud?.updatedAt && new Date(aud.updatedAt),
    ['desc'],
  );

  const audiences =
    audienceDetails &&
    audienceDetails?.id === activeAudienceId &&
    !audiencesSorted.find(a => a.id === audienceDetails?.id)
      ? [audienceDetails, ...audiencesSorted] // we need this when navigate to audience id that isn't among entities
      : audiencesSorted;

  const hasAudiences = Boolean(audiences.length);
  const [filter, setFilter] = useState<string>();
  const debouncedFilter = useDebounce(filter, 300);
  const { fetch, pagination, isFetching } = useFetchAudiences({
    skipBusy: true,
    withoutSystemFilters: false,
    fitchFirstPageOnInit: true,
    withoutfetchMore: true,
    inUse: true,
  });
  const { setAudienceId } = useLeadContactsFilter();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const sortingOptions = [
    {
      id: 'created_at/desc',
      name: 'Newest',
    },
    {
      id: 'created_at/asc',
      name: 'Oldest',
    },
    {
      id: 'contacts_count/desc',
      name: 'Largest',
    },
    {
      id: 'contacts_count/asc',
      name: 'Smallest',
    },
  ];

  const [sorting, setSorting] = useState<DropdownOption>(sortingOptions[0]);

  const onSortingChange = (option: DropdownOption) => {
    const optionIdArray = option.id.toString().split('/');
    const sortColumn = optionIdArray[0] as any;
    const sortDirection = optionIdArray[1] as any;
    fetch(1, 10, undefined, { sortColumn, sortDirection, inUse: true });
  };

  const onChangePagination = (pageNumber: number) => {
    fetch(pageNumber, 10, undefined, { inUse: true });
    setAudienceId(undefined);
  };

  useEffect(() => {
    if (debouncedFilter !== undefined) {
      fetch(1, 10, debouncedFilter, { inUse: true });
    }
  }, [debouncedFilter, fetch]);

  return (
    <div className={classNames(styles.container)} ref={containerRef}>
      {(hasAudiences || filter) && (
        <div className={styles.toolbar}>
          <SearchInput
            css={{
              container: styles.searchInput,
              icon: styles.searchIcon,
            }}
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Search audience"
          />
          <div className={styles.sorter}>
            <span className={styles.sorterLabel}>Filtered by:</span>
            <PrimarySelect
              className={styles.sortingSelect}
              options={sortingOptions}
              value={sorting}
              onChange={option => {
                setSorting(option);
                onSortingChange(option);
              }}
              customDropdownAlign="end"
            />
          </div>
        </div>
      )}

      {hasAudiences ? (
        <div className={classNames(styles.container)}>
          {audiences.map(audience => (
            <AudienceAccordionItem
              audience={audience}
              key={audience.id}
              processClick={processClick}
              fetchContacts={fetchContacts}
              activeAudienceId={activeAudienceId}
              busy={busy}
            />
          ))}
        </div>
      ) : (
        !isFetching &&
        (filter ? (
          <div className={styles.noResultsBlock}>
            <div className={styles.noResultsTitle}>
              No audiences match your search
            </div>

            <div>
              Edit current search value or{' '}
              <span
                className={styles.noResultsLink}
                onClick={() => setFilter('')}
              >
                reset it
              </span>
            </div>

            <EmptyIcon className={styles.noResultsIcon} />
          </div>
        ) : (
          <div className={styles.emptyBlock}>
            <div className={styles.emptyBlockTitle}>
              Find out how audience builder can help you get control of your
              leads
            </div>
            <div className={styles.emptyBlockDescription}>
              {[
                'Reach out to the most engaged contacts',
                'Export your hot leads',
                'Track your automations performance',
              ].map(item => (
                <div className={styles.row} key={item}>
                  <CheckOutlined className={styles.checkIcon} />
                  {item}
                </div>
              ))}
            </div>
            <img src={emptyStateImg} className={styles.emptyBlockImage} />
            <Button
              css={{ container: styles.primaryButton }}
              onClick={onCreateClick}
            >
              Build an Audience
            </Button>
          </div>
        ))
      )}
      <div className={styles.pagination}>
        <Pagination
          current={pagination.page}
          onChange={onChangePagination}
          pageSize={pagination.perPage}
          showSizeChanger={false}
          total={pagination.total}
          disabled={isFetching}
          hideOnSinglePage
        />
      </div>
    </div>
  );
};
